import React from 'react';

import type { FingeringSize } from './FingeringOverlay';

const TIERS: FingeringSize[] = ['small', 'medium', 'large'];
const TIER_TO_INDEX: Record<FingeringSize, number> = {
  small: 0,
  medium: 1,
  large: 2,
};

// Knob diameter in pixels. Doubled as `KNOB_RADIUS` to inset the
// track / fill / tick row so the knob's outer edge never leaves the
// pill at the extremes (left=0 / right=100%).
const KNOB_SIZE = 24;
const KNOB_RADIUS = KNOB_SIZE / 2;

/** Fingering digits on the score: `#5c6bff` on `rgb(238, 241, 254)`. */
const FINGERING_BLUE = 'text-[#5c6bff]';
const FINGERING_PILL = 'rounded-full bg-[rgb(238,241,254)]';
/** Pitch letters drawn off the notehead. */
const PITCH_BROWN = 'text-[#8B4513]';
/** Pitch letters drawn on the notehead: white on a black circle. */
const PITCH_ON_NOTE = 'rounded-full bg-black text-white';

export interface FingeringSizeSliderProps {
  value: FingeringSize;
  onChange: (value: FingeringSize) => void;
  ariaLabel?: string;
  /** Mark at each end. Fingering uses a number; pitch letters use A. */
  glyph?: '3' | 'A';
  /** Drop the pill so a parent card can hold more than one slider. */
  bare?: boolean;
}

/**
 * 3-stop slider for a fingering or pitch-letter size tier.
 * A small glyph and a large glyph mark the ends. Fingering uses "3";
 * pitch letters use "A". The track is grey, with the active portion
 * filled in soft yellow (`#ffec83`),
 * three discrete stop ticks below the track, and a white circular
 * knob with a soft drop-shadow at the current value's position.
 *
 * Implementation notes:
 *   - The visual track / fill / ticks / knob are all absolutely-
 *     positioned divs so we get full styling control without
 *     fighting `<input type="range">`'s vendor-specific shadow DOM.
 *   - A fully-transparent native `<input type="range">` is overlaid
 *     on top, giving us free keyboard support (arrow keys), screen-
 *     reader value announcements (`aria-valuetext`), and click /
 *     drag interaction without writing pointer handlers ourselves.
 *   - The knob's `left` is in `calc((100% - 24px) * pct)` form so its
 *     OUTER edge stays inside the track at the extremes; the visual
 *     track is inset by `KNOB_RADIUS` on each side for the same
 *     reason. This is the standard "knob clamping" trick — it keeps
 *     the knob centred over its stop tick at all positions.
 */
export const FingeringSizeSlider: React.FC<FingeringSizeSliderProps> = ({
  value,
  onChange,
  ariaLabel = 'Fingering size',
  glyph = '3',
  bare = false,
}) => {
  const sliderValue = TIER_TO_INDEX[value];
  const percent = (sliderValue / (TIERS.length - 1)) * 100;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const idx = Math.max(
      0,
      Math.min(TIERS.length - 1, Math.round(Number(e.target.value)))
    );
    onChange(TIERS[idx]);
  };

  const endMark = (size: 'sm' | 'lg') => (
    <span
      aria-hidden
      className={`
        inline-flex items-center justify-center leading-none font-semibold
        ${
          glyph === '3'
            ? `
              ${FINGERING_PILL}
              ${FINGERING_BLUE}
              ${size === 'sm' ? 'h-5 min-w-5 text-xs' : 'h-8 min-w-8 text-lg'}
            `
            : size === 'sm'
              ? `${PITCH_ON_NOTE} h-5 min-w-5 text-xs`
              : `${PITCH_BROWN} text-lg`
        }
      `}
    >
      {glyph}
    </span>
  );

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      title={ariaLabel}
      className={`
        flex items-center gap-x-3
        ${
          bare
            ? 'h-10 w-full'
            : `
              h-[50px] w-[280px] rounded-full bg-white px-4
              shadow-[0_8px_40px_rgba(0,0,0,0.12)]
            `
        }
      `}
    >
      {/* The small mark is the low end of the slider. */}
      {endMark('sm')}

      <div className="relative h-6 flex-1">
        {/* Track — neutral grey bar centred vertically. Inset by the
            knob radius so the knob's outer edge sits flush with the
            track's start / end when the slider is at min / max. */}
        <div
          className={`
            absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-gray-500/30
          `}
          style={{ left: KNOB_RADIUS, right: KNOB_RADIUS }}
        />

        {/* Fill — yellow segment from the track's left edge to the
            knob centre. `width` is computed in the same inset
            coordinate space the knob uses so the fill always ends
            exactly under the knob. */}
        <div
          className={`
            absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-[#ffec83]
          `}
          style={{
            left: KNOB_RADIUS,
            width: `calc((100% - ${KNOB_SIZE}px) * ${percent / 100})`,
          }}
        />

        {/* Stop ticks — three small dots BELOW the track at each
            discrete stop position. Same `left` / `right` inset as
            the track so the outermost dots sit directly beneath the
            knob's extreme positions. */}
        <div
          aria-hidden
          className={`
            pointer-events-none absolute top-1/2 mt-[10px] flex justify-between
          `}
          style={{ left: KNOB_RADIUS, right: KNOB_RADIUS }}
        >
          {TIERS.map((tier) => (
            <span key={tier} className="size-1 rounded-full bg-gray-600/40" />
          ))}
        </div>

        {/* Knob — white circle with a soft two-layer shadow. The
            shadow values are copied verbatim from the Figma node
            (`0px 0.5px 4px rgba(0,0,0,0.12)` + the bigger
            `0px 6px 13px` ambient) so the knob reads as a real
            "elevated" surface and not a flat disc. */}
        <div
          className={`
            pointer-events-none absolute top-1/2 -translate-y-1/2 rounded-full
            bg-white
            shadow-[0_0.5px_4px_rgba(0,0,0,0.12),0_6px_13px_rgba(0,0,0,0.12)]
          `}
          style={{
            width: KNOB_SIZE,
            height: KNOB_SIZE,
            left: `calc((100% - ${KNOB_SIZE}px) * ${percent / 100})`,
          }}
        />

        {/* Invisible native range — sits on top of the painted track
            and forwards all keyboard / pointer interaction. Keeps
            arrow-key support, value-text announcements, and free
            drag-to-set behaviour without a slider library. */}
        <input
          type="range"
          min={0}
          max={TIERS.length - 1}
          step={1}
          value={sliderValue}
          onChange={handleChange}
          aria-label={`${ariaLabel} slider`}
          aria-valuetext={value}
          className="absolute inset-0 w-full cursor-pointer opacity-0"
        />
      </div>

      {/* The large mark is the high end of the slider. */}
      {endMark('lg')}
    </div>
  );
};
